import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const sections = [
    {
        id: "intro",
        title: "1. Introduction",
        body: [
            "PipeGuide (\"we\", \"us\") respects your privacy. This Privacy Policy explains how we collect, use and protect personal information when you use our Service. We comply with the Protection of Personal Information Act (POPIA) and, where applicable, the General Data Protection Regulation (GDPR).",
        ],
    },
    {
        id: "data-we-collect",
        title: "2. Data We Collect",
        body: [
            "Account data: name, work email, organisation, role, and authentication identifiers (Google SSO ID where applicable).",
            "Call data: live transcripts, qualification scores, AI-generated suggestions and summaries. Audio is processed in-memory and not stored unless you explicitly enable recording.",
            "Usage data: pages viewed, features used, anonymised IP address and device information for security and analytics.",
        ],
    },
    {
        id: "how-we-use",
        title: "3. How We Use Your Data",
        body: [
            "To provide the Service: scoring calls, generating suggestions, sending digests, and pushing data to your CRM.",
            "To improve the Service: aggregated and de-identified data may be used to improve our AI models. Customer Data is never shared with third parties for their own marketing purposes.",
            "To communicate with you: transactional emails (account, billing) and product updates.",
        ],
    },
    {
        id: "sharing",
        title: "4. Sharing of Data",
        body: [
            "We share data only with sub-processors that help us run the Service (cloud hosting, payment processing, AI inference). A current list is available on request.",
            "Customer Data is never shared between PipeGuide tenants. Each organisation is fully isolated.",
        ],
    },
    {
        id: "retention",
        title: "5. Data Retention",
        body: [
            "Active account data is retained while you are a customer. Upon termination, data is retained for 30 days then deleted from production systems and from backups within a further 60 days.",
            "Anonymised aggregate metrics may be retained indefinitely.",
        ],
    },
    {
        id: "rights",
        title: "6. Your Rights",
        body: [
            "Under POPIA and GDPR, you may request access to, correction or deletion of your personal information; restrict processing; or lodge a complaint with the Information Regulator (South Africa) or your local data protection authority.",
            "Email support@support.pipeguide.tech to exercise these rights.",
        ],
    },
    {
        id: "security",
        title: "7. Security",
        body: [
            "We use TLS 1.2+ for connections, JWT-based authentication, encryption at rest for sensitive fields, and strict role-based access controls. We perform regular vulnerability scans and require strong passwords or SSO.",
        ],
    },
    {
        id: "dpa",
        title: "8. Data Processing Addendum",
        body: [
            "Customers on Pro and Business plans may execute our Data Processing Addendum (DPA), which governs our role as a processor of personal information on your behalf. Email support@support.pipeguide.tech to request a copy.",
        ],
    },
    {
        id: "contact",
        title: "9. Contact",
        body: [
            "For privacy questions, email support@support.pipeguide.tech.",
        ],
    },
];

export default function Privacy() {
    return (
        <div data-testid="privacy-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Legal</p>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">Privacy Policy</h1>
                    <p className="mt-4 text-zinc-600">Last updated: 1 December 2025</p>
                </div>
            </section>

            <section className="bg-white flex-1">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-3">
                        <div className="sticky top-24">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">On this page</p>
                            <nav className="space-y-2">
                                {sections.map((s) => (
                                    <a key={s.id} href={`#${s.id}`} data-testid={`privacy-nav-${s.id}`} className="block text-sm text-zinc-600 hover:text-brand-primary">
                                        {s.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <article className="lg:col-span-9">
                        {sections.map((s) => (
                            <div key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`} className="mb-10 scroll-mt-24">
                                <h2 className="font-display text-2xl font-bold text-zinc-900">{s.title}</h2>
                                {s.body.map((p, i) => (
                                    <p key={i} className="mt-4 text-zinc-700 leading-relaxed">{p}</p>
                                ))}
                            </div>
                        ))}
                    </article>
                </div>
            </section>

            <Footer />
        </div>
    );
}
